/**
 * pdfReport.js — professional PDF position report generator.
 *
 * Renders the StockSummary screen's filtered data: a summary block on top,
 * open positions, pending-settlement positions, and closed-position groups
 * with their underlying transactions. Data arrives pre-shaped from the page
 * (all P/L strings already formatted) so this module stays view-agnostic.
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const NAVY = [30, 58, 105];
const LIGHT = [240, 243, 249];
const GREEN = [16, 122, 63];
const RED = [176, 34, 34];
const GRAY = [105, 115, 130];
const BORDER = [210, 215, 225];

const MARGIN = 14;
const PAGE_W = 297; // A4 landscape
const CONTENT_TOP = 28; // page 1 (below the header band)
const CONT_TOP = 22;    // continuation pages

// P/L cells come in pre-formatted ("+$1,234.56" / "-$1,234.56") — color by sign.
const plCellStyle = (data) => {
  const raw = String(data.cell.raw ?? '').trim();
  if (raw.startsWith('-')) data.cell.styles.textColor = RED;
  else if (raw.startsWith('+')) data.cell.styles.textColor = GREEN;
};

// jsPDF's built-in fonts are Latin-1 only — replace unsupported glyphs.
const cleanText = (s) => String(s ?? '')
  .replace(/→/g, '-')
  .replace(/…/g, '...')
  .replace(/[‘’]/g, "'")
  .replace(/[“”]/g, '"')
  .replace(/[—–]/g, '-')
  .replace(/[^\t\n -ÿ]/g, '?');

const sanitizeCell = (d) => {
  if (d.cell?.text) d.cell.text = d.cell.text.map(cleanText);
};

const baseTable = {
  theme: 'grid',
  margin: { left: MARGIN, right: MARGIN, top: CONT_TOP, bottom: 14 },
  styles: { fontSize: 7.5, cellPadding: 1.6, lineColor: BORDER, lineWidth: 0.15 },
  headStyles: { fillColor: NAVY, textColor: 255, fontSize: 7.5, fontStyle: 'bold' },
  alternateRowStyles: { fillColor: LIGHT },
  didParseCell: sanitizeCell,
};

const sectionTitle = (doc, text, y) => {
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(NAVY[0], NAVY[1], NAVY[2]);
  doc.text(cleanText(text), MARGIN, y);
  return y + 3;
};

const ensureSpace = (doc, y, needed = 20) => {
  if (y + needed > doc.internal.pageSize.getHeight() - 14) {
    doc.addPage();
    return CONT_TOP;
  }
  return y;
};

/**
 * @param {{
 *   title: string,
 *   subtitle?: string,
 *   generatedAt?: string,
 *   kpis?: { label: string, value: string }[],
 *   openSections?: { heading: string, head: string[], rows: string[][], foot?: string[] }[],
 *   pending?: { head: string[], rows: string[][] } | null,
 *   closedGroups?: { heading: string, meta?: string, head: string[], rows: string[][] }[],
 *   closedTotal?: string,
 *   plColumns?: Record<string, true>, // header names whose cells get +/- coloring
 *   filename?: string,
 * }} cfg
 */
export function generatePositionsReport(cfg) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const plCols = cfg.plColumns || {};

  // ── Header band (page 1) ──────────────────────────────────────────────
  doc.setFillColor(NAVY[0], NAVY[1], NAVY[2]);
  doc.rect(0, 0, PAGE_W, 22, 'F');
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(cleanText(cfg.title || 'Portfolio Report'), MARGIN, 10);
  if (cfg.subtitle) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(cleanText(cfg.subtitle), MARGIN, 17);
  }
  doc.setFontSize(8);
  doc.text(cleanText(`Generated: ${cfg.generatedAt || new Date().toLocaleString()}`), PAGE_W - MARGIN, 10, { align: 'right' });
  doc.text('CONFIDENTIAL', PAGE_W - MARGIN, 17, { align: 'right' });

  let y = CONTENT_TOP;

  // ── Summary KPIs ──────────────────────────────────────────────────────
  if (cfg.kpis?.length) {
    y = ensureSpace(doc, y, 18);
    y = sectionTitle(doc, 'Summary', y);
    autoTable(doc, {
      ...baseTable,
      startY: y + 2,
      head: [cfg.kpis.map(k => k.label)],
      body: [cfg.kpis.map(k => k.value)],
      headStyles: { ...baseTable.headStyles, halign: 'center' },
      bodyStyles: { halign: 'center', fontStyle: 'bold', fontSize: 8.5 },
      didParseCell: (d) => { sanitizeCell(d); plCellStyle(d); },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ── Open positions ────────────────────────────────────────────────────
  if (cfg.openSections?.length) {
    y = ensureSpace(doc, y, 22);
    y = sectionTitle(doc, `Open Positions (${cfg.openSections.reduce((n, s) => n + s.rows.length, 0)})`, y);
    cfg.openSections.forEach(sec => {
      y = ensureSpace(doc, y, 14);
      if (sec.heading) {
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(GRAY[0], GRAY[1], GRAY[2]);
        doc.text(cleanText(sec.heading), MARGIN, y + 4);
      }
      autoTable(doc, {
        ...baseTable,
        startY: y + (sec.heading ? 6 : 2),
        head: [sec.head],
        body: sec.rows,
        ...(sec.foot ? { foot: [sec.foot] } : {}),
        footStyles: { fillColor: [226, 232, 244], textColor: NAVY, fontStyle: 'bold', fontSize: 7.5 },
        didParseCell: (d) => { sanitizeCell(d); if (plCols[sec.head[d.column.index]]) plCellStyle(d); },
      });
      y = doc.lastAutoTable.finalY + 4;
    });
  }

  // ── Closed pending (recently closed, awaiting settlement) ─────────────
  if (cfg.pending?.rows?.length) {
    y = ensureSpace(doc, y, 20);
    y = sectionTitle(doc, `Closed Pending Settlement (${cfg.pending.rows.length})`, y + 2);
    autoTable(doc, {
      ...baseTable,
      startY: y + 2,
      head: [cfg.pending.head],
      body: cfg.pending.rows,
      didParseCell: (d) => { sanitizeCell(d); if (plCols[cfg.pending.head[d.column.index]]) plCellStyle(d); },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ── Closed positions with transactions ────────────────────────────────
  if (cfg.closedGroups?.length) {
    y = ensureSpace(doc, y, 26);
    y = sectionTitle(doc, `Closed Positions — Transaction Detail (${cfg.closedGroups.length})`, y + 2);
    cfg.closedGroups.forEach(g => {
      y = ensureSpace(doc, y, 24);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(NAVY[0], NAVY[1], NAVY[2]);
      doc.text(cleanText(g.heading), MARGIN, y + 4);
      if (g.meta) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(GRAY[0], GRAY[1], GRAY[2]);
        doc.text(cleanText(g.meta), MARGIN + doc.getTextWidth(g.heading) + 4, y + 4);
      }
      autoTable(doc, {
        ...baseTable,
        startY: y + 6,
        head: [g.head],
        body: g.rows,
        didParseCell: (d) => { sanitizeCell(d); if (plCols[g.head[d.column.index]]) plCellStyle(d); },
      });
      y = doc.lastAutoTable.finalY + 5;
    });
    if (cfg.closedTotal) {
      y = ensureSpace(doc, y, 10);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(NAVY[0], NAVY[1], NAVY[2]);
      doc.text(cleanText(cfg.closedTotal), MARGIN, y + 4);
      y += 8;
    }
  }

  // ── Footer on every page ──────────────────────────────────────────────
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(GRAY[0], GRAY[1], GRAY[2]);
    const h = doc.internal.pageSize.getHeight();
    doc.text('MyTradesHub - Position Report', MARGIN, h - 6);
    doc.text(`Page ${i} of ${pages}`, PAGE_W - MARGIN, h - 6, { align: 'right' });
  }

  doc.save(cfg.filename || 'portfolio-report.pdf');
}
